import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { SiteFooter } from './components/layout/SiteFooter';
import { SiteHeader } from './components/layout/SiteHeader';
import { OfflineBar } from './components/layout/OfflineBar';
import { Spinner } from './components/ui';
import { useAuth } from './lib/auth';
import { HomeRoute } from './pages/HomeRoute';
import { VisitBeacon } from './lib/visits';

// Everything behind the landing page is code-split: a first-time visitor
// downloads the marketing page and nothing else.
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const RecoverPage = lazy(() => import('./pages/RecoverPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const DealsPage = lazy(() => import('./pages/DealsPage'));
const TripPage = lazy(() => import('./pages/TripPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const InvitePage = lazy(() => import('./pages/InvitePage'));

export function App() {
  return (
    <div className="flex min-h-screen flex-col">
      <ScrollToTop />
      <VisitBeacon />
      <SiteHeader />
      <OfflineBar />
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/gallery" element={<GalleryPage />} />
            {/* Open without an account; building a trip from a deal asks to sign in. */}
            <Route path="/deals" element={<DealsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />

            <Route path="/login" element={<GuestOnly><LoginPage /></GuestOnly>} />
            <Route path="/register" element={<GuestOnly><RegisterPage /></GuestOnly>} />
            <Route path="/recover" element={<GuestOnly><RecoverPage /></GuestOnly>} />

            {/* Public and shared trip views — deliberately outside the auth gate. */}
            <Route path="/t/:handle/:slug" element={<TripPage />} />
            <Route path="/s/:token" element={<TripPage shared />} />
            {/* An invite to co-author: shows the card, accepting asks to sign in. */}
            <Route path="/invite/:token" element={<InvitePage />} />

            <Route path="/app" element={<RequireAuth><DashboardPage /></RequireAuth>} />
            <Route path="/app/trips/:tripId" element={<RequireAuth><TripPage owner /></RequireAuth>} />
            <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />
            <Route path="/admin" element={<RequireAuth admin><AdminPage /></RequireAuth>} />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <SiteFooter />
    </div>
  );
}

function PageLoader() {
  return (
    <div className="grid min-h-[60vh] place-items-center">
      <Spinner className="size-8 text-clay-500" />
    </div>
  );
}

/** Anchor links inside the landing page must not be hijacked by this. */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname, hash]);
  return null;
}

function RequireAuth({ children, admin }: { children: React.ReactNode; admin?: boolean }) {
  const { status, isAdmin } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <PageLoader />;
  if (status === 'anonymous') {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  if (admin && !isAdmin) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function GuestOnly({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  if (status === 'loading') return <PageLoader />;
  if (status === 'authenticated') return <Navigate to="/app" replace />;
  return <>{children}</>;
}
